import { useState } from 'react';
import cn from 'classnames';
// Types
import { StreamRecordDisplay } from 'helpers/streams';
// Helpers
import { computeDuration } from 'helpers/time';
// UI
import Image from 'ui/Image/Image';
import Icon from 'ui/Icon/Icon';
// UI3
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Icons
import PlayVideoIcon from '../../../../../../../public/images/watch-stream-images/play-video-player.svg';
import PauseVideoIcon from '../../../../../../../public/images/watch-stream-images/pause-video-player.svg';
// Styles
import styles from './VideoPlaylist.module.scss';

type VideoPlaylistProps = {
  recordsList: StreamRecordDisplay[];
  currentVideoIndex: number;
  onRecordClick: (record: StreamRecordDisplay) => void;
};

const VideoPlaylist = ({
  recordsList,
  currentVideoIndex,
  onRecordClick,
}: VideoPlaylistProps): JSX.Element => {
  const [isPlaylistOpen, setIsPlaylistOpen] = useState(false);

  const handlePlaylistToggle = () => {
    setIsPlaylistOpen((prev) => !prev);
  };

  const playlistCounter = currentVideoIndex + 1;

  if (!recordsList.length) {
    return <></>;
  }

  return (
    <div className={styles.playlistContainer}>
      <div className={styles.playlistAccordion}>
        <div className={styles.playlistHeader}>
          <button
            className={styles.playlistToggle}
            onClick={handlePlaylistToggle}
            aria-expanded={isPlaylistOpen}
            aria-label={isPlaylistOpen ? 'Hide playlist' : 'Show playlist'}
          >
            <div className={styles.playlistTitleSection}>
              <h2 className={styles.playlistTitle}>Stream Playlist</h2>
              <span className={styles.playlistCount}>
                <MillionsIcon
                  name="videoPlaylist"
                  className={styles.playlistIcon}
                  size={12}
                />
                {playlistCounter}/{recordsList.length} videos
              </span>
            </div>
            <Icon
              name={isPlaylistOpen ? 'chevron-down' : 'chevron-up'}
              className={styles.chevronIcon}
            />
          </button>
        </div>

        {isPlaylistOpen && (
          <div className={styles.playlistContent}>
            <div className={styles.playlistCardsContainer}>
              {recordsList.map((item) => {
                const isActiveRecord =
                  recordsList[currentVideoIndex]?.videoURL === item.videoURL;

                if (!item.videoURL) {
                  return null;
                }

                return (
                  <div
                    key={item.id}
                    className={cn(styles.card, {
                      [styles.active]: isActiveRecord,
                    })}
                    aria-label="stream preview card"
                  >
                    <Image
                      src={item.videoThumbnailURL || ''}
                      alt="stream preview"
                      className={styles.cardVideo}
                      loading="lazy"
                    />
                    {item.duration && (
                      <span className={styles.duration}>
                        {computeDuration(item.duration)}
                      </span>
                    )}
                    {!isActiveRecord ? (
                      <button
                        className={styles.videoButton}
                        aria-label="play video"
                        onClick={() => onRecordClick(item)}
                      >
                        <PlayVideoIcon className={styles.videoIcon} />
                      </button>
                    ) : (
                      <button
                        className={styles.videoButton}
                        aria-label="pause video"
                        onClick={() => onRecordClick(item)}
                      >
                        <PauseVideoIcon
                          className={cn(styles.videoIcon, styles.pauseIcon)}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlaylist;
